// External modules
import throttle from 'lodash.throttle';

// Internal modules
import { appStore } from '@/store';
import { SESSION_ID } from './constants';
import { apolloClient } from '@/lib/apollo';
import { ADDRESS_METADATA_QUERY } from '@/lib/graphql';
import {
  features,
  formatGriffinAddressMetadata,
  getAddressMetaKey,
  getContent,
  getGlobals,
  getLocale,
  logger,
  redirectToGenericError,
  validateGraphQLResponse,
} from '@/lib';
import {
  ADDRESS_AUTO_COMPLETE_SELECT,
  ADDRESS_AUTO_COMPLETE_SUGGEST,
} from './fpti';
import {
  ADDRESS_AUTOCOMPLETE_QUERY,
  ADDRESS_FROM_AUTOCOMPLETE_PLACE_ID_QUERY,
} from './graphql';

const getAddressAutocomplete = ({ addressType, country, input }) => {
  const { language } = getLocale();
  const {
    user: { geolocation = {} },
  } = getGlobals();
  const { geoCountry, latitude, longitude } = geolocation;

  // Pass latitude longitude only if user selected country is
  // same as his ip address country
  const isValidLatLong = Boolean(
    geoCountry === country && latitude && longitude,
  );
  const variables = {
    count: features.isActive('hasAddressAutoCompleteFiveItems') ? 5 : 4, // Limit response to 4 or 5 addresses based on the treatment
    countries: [country],
    input,
    language,
    radius: 1500, // approx. 1 mile in meters
    sessionId: SESSION_ID,
  };

  const dontSeeMyAddressOption = {
    addressText: '',
    mainText: getContent('addressSuggestion.mainText'),
    placeId: 'IDONTSEEIT',
    secondaryText: '',
  };

  if (isValidLatLong) {
    // toFixed(3) as the 3rd decimal digit represents an area of
    // 110 m around users actual location
    // NOTE: do not send user's pin point location as it is PII
    variables.location = `${latitude.toFixed(3)},${longitude.toFixed(3)}`;
  }

  logger.fpti(
    'gaac_autocomplete_init',
    ADDRESS_AUTO_COMPLETE_SUGGEST({
      addressType,
      charCount: input.length,
      country,
      geoCountry,
      type: 'prepare',
    }),
  );
  const hasAddressAutoCompleteSingleLine = features.isActive(
    'hasAddressAutoCompleteSingleLine',
  );

  return apolloClient
    .query({
      query: ADDRESS_AUTOCOMPLETE_QUERY,
      variables,
    })
    .then(response => {
      const fptiLogObj = ADDRESS_AUTO_COMPLETE_SUGGEST({
        addressType,
        charCount: input.length,
        country,
        errors: response.errors,
        geoCountry,
        type: 'process',
      });

      logger.fpti('gaac_autocomplete_done', fptiLogObj);
      const result = response.data?.addressAutoComplete?.suggestions || [];
      if (
        !result.find(item => item.placeId === 'IDONTSEEIT') &&
        hasAddressAutoCompleteSingleLine
      ) {
        result.push(dontSeeMyAddressOption);
      }
      return result;
    })
    .catch(error => {
      // Ignore all errors
      return hasAddressAutoCompleteSingleLine ? [dontSeeMyAddressOption] : [];
    });
};

// Throttle timeout is set to 250 ms as normal typing speed
// is 250 ms per character and also API response time is around
// 200 ms on an average
const throttledGetAddressAutocomplete = throttle(
  (args, setSuggestedAddressList) => {
    logger.info('gaac_autocomplete_throttled_api_triggered');
    // All the errors are handled in getAddressAutocomplete fn
    getAddressAutocomplete(args).then(result => {
      setSuggestedAddressList(result);
    });
  },
  250,
);

const getAddressFromAutocompletePlaceId = ({
  addressIndex,
  addressType,
  placeId,
}) => {
  const { language } = getLocale();
  const variables = {
    language,
    placeId,
    sessionId: SESSION_ID,
  };

  logger.fpti(
    'gaac_place_id_to_address_init',
    ADDRESS_AUTO_COMPLETE_SELECT({
      addressIndex,
      addressType,
      type: 'prepare',
    }),
  );

  return apolloClient
    .query({
      query: ADDRESS_FROM_AUTOCOMPLETE_PLACE_ID_QUERY,
      variables,
    })
    .then(response => {
      const { addressFromAutoCompletePlaceId = {} } = response.data;
      const { address = {} } = addressFromAutoCompletePlaceId;
      const fptiLogObj = ADDRESS_AUTO_COMPLETE_SELECT({
        address,
        addressIndex,
        addressType,
        errors: response.errors,
        type: 'process',
      });

      logger.fpti('gaac_place_id_to_address_done', fptiLogObj);

      return address;
    })
    .catch(error => {
      logger.warn('gaac_address_select_failed', {
        message: error.message,
      });

      // Ignore all errors
      return {};
    });
};

const fetchAddressMetadata = countryCode => {
  const { address: addressMetadata } = appStore.getState().api.griffinMetadata;
  const { language: languageCode } = getLocale();
  const addressMetaKey = getAddressMetaKey(countryCode);

  // Only fetch metadata if we don't already have it in the store
  if (addressMetadata.hasOwnProperty(addressMetaKey)) {
    return Promise.resolve(addressMetadata[addressMetaKey]);
  }

  return apolloClient
    .query({
      query: ADDRESS_METADATA_QUERY,
      variables: { countryCode, languageCode },
    })
    .then(response => {
      /**
       * @TO_DO @TECH_DEBT
       * Temporarily leaving this check until we refactor our error handling
       */
      validateGraphQLResponse(
        response,
        'address_metadata',
        'ADDRESS_META_ERROR',
      );

      const { localeMetadata = {} } = response.data;
      const addressMeta = formatGriffinAddressMetadata(localeMetadata.address);
      appStore.getActions().api.addAddressMetadata({
        key: addressMetaKey,
        payload: addressMeta,
      });

      return addressMeta;
    })
    .catch(error => {
      logger.warn('weasley_address_metadata_error', {
        message: error.message,
      });

      redirectToGenericError();
    });
};

export {
  fetchAddressMetadata,
  getAddressFromAutocompletePlaceId,
  throttledGetAddressAutocomplete,
};
